import { useState } from "react";
import { useSession } from "@/services/auth0/session";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { BusinessAccount } from "@/api.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

interface SelectedAddress {
  addressComponents: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode?: string;
    country: string;
  };
  location: {
    lat: number;
    lng: number;
  };
}

const businessAccountSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  taxNumber: z.string().optional(),
  address: z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    city: z.string().min(1),
    province: z.string().min(1),
    postalCode: z.string().optional(),
    country: z.string().min(1),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
});

type BusinessAccountSchema = z.infer<typeof businessAccountSchema>;

export default function BusinessAccountScreen() {
  const { toast } = useToast();
  const { customer, token } = useSession();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<BusinessAccountSchema>({
    resolver: zodResolver(businessAccountSchema),
    defaultValues: {
      companyName: customer?.businessAccount?.companyName ?? "",
      taxNumber: customer?.businessAccount?.taxNumber ?? "",
      address: customer?.businessAccount?.address ?? undefined,
    },
  });

  const createBusinessAccountMutation = useMutation({
    mutationFn: async (data: BusinessAccountSchema) => {
      const response = await axios.post<BusinessAccount>(
        `${import.meta.env.VITE_API_URL}/api/v1/business-account`,
        {
          ...data,
          customerId: customer?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "Business account created successfully",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create business account",
      });
    },
  });

  const updateBusinessAccountMutation = useMutation({
    mutationFn: async (data: BusinessAccountSchema) => {
      const response = await axios.patch<BusinessAccount>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/business-accounts/${customer?.businessAccount?.id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "Business account updated successfully",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update business account",
      });
    },
  });

  const handleSubmit = (values: BusinessAccountSchema) => {
    if (customer?.businessAccount) {
      updateBusinessAccountMutation.mutate(values);
    } else {
      createBusinessAccountMutation.mutate(values);
    }
  };

  return (
    <div className="container max-w-xl space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">Business Account</h2>
        <p className="text-sm text-muted-foreground">
          {customer?.businessAccount
            ? "Update your business account details"
            : "Set up your business account"}
        </p>
      </div>

      {!customer?.businessAccount ? (
        <Card className="bg-gray-50 border-2 border-dashed">
          <CardContent className="pt-6">
            <div className="text-center py-6">
              <p className="text-lg font-medium">No Business Account</p>
              <p className="text-sm text-gray-500 mt-1">
                Create a business account to access additional features and
                credit terms
              </p>
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                className="mt-4"
              >
                Create Business Account
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          <form className="space-y-4">
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="border-0 bg-gray-50"
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taxNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Number</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="border-0 bg-gray-50"
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Address</FormLabel>
                  <FormControl>
                    <Input
                      value={[
                        field.value?.line1,
                        field.value?.line2,
                        [
                          field.value?.city,
                          field.value?.province,
                          field.value?.postalCode,
                        ]
                          .filter(Boolean)
                          .join(", "),
                        field.value?.country,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      className="border-0 bg-gray-50"
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              type="button"
              className="w-full"
            >
              Edit Business Account
            </Button>
          </form>
        </Form>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {customer?.businessAccount
                ? "Update Business Account"
                : "Create Business Account"}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              className="space-y-4"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input {...field} className="border-0 bg-gray-50" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taxNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax Number (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} className="border-0 bg-gray-50" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Business Address</FormLabel>
                <AutocompleteAddress
                  onSelectedAddress={(
                    selectedAddress: SelectedAddress | null
                  ) => {
                    if (selectedAddress) {
                      form.setValue("address", {
                        line1: selectedAddress.addressComponents.line1,
                        line2: selectedAddress.addressComponents.line2,
                        city: selectedAddress.addressComponents.city,
                        province: selectedAddress.addressComponents.state,
                        postalCode:
                          selectedAddress.addressComponents.postalCode ?? "",
                        country: selectedAddress.addressComponents.country,
                        location: selectedAddress.location,
                      });
                    }
                  }}
                  placeholder="Search business address"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={
                  createBusinessAccountMutation.isPending ||
                  updateBusinessAccountMutation.isPending
                }
              >
                {createBusinessAccountMutation.isPending ||
                updateBusinessAccountMutation.isPending
                  ? "Processing..."
                  : customer?.businessAccount
                    ? "Update Business Account"
                    : "Create Business Account"}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
